import React, { useEffect, useRef } from "react";

const CELL = 30;
const BASE_ALPHA = 0.05;

/**
 * The hero's ground: a sparse lattice of pixels that echoes the dissolving
 * squares in the wordmark. A slow diagonal wave keeps it alive; the pointer
 * lifts the cells nearest it. Static single frame under reduced motion.
 */
const SignalField: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pointer = useRef({ x: -9999, y: -9999, active: false });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    const reduced =
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

    let w = 0;
    let h = 0;
    let cols = 0;
    let rows = 0;
    let raf = 0;
    let running = true;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      const parent = canvas.parentElement;
      w = parent?.clientWidth ?? window.innerWidth;
      h = parent?.clientHeight ?? window.innerHeight;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cols = Math.ceil(w / CELL) + 1;
      rows = Math.ceil(h / CELL) + 1;
    };

    resize();
    window.addEventListener("resize", resize);

    const onPointer = (e: PointerEvent) => {
      const r = canvas.getBoundingClientRect();
      pointer.current = {
        x: e.clientX - r.left,
        y: e.clientY - r.top,
        active: true,
      };
    };
    const onLeave = () => {
      pointer.current.active = false;
    };

    if (!reduced) {
      window.addEventListener("pointermove", onPointer, { passive: true });
      window.addEventListener("pointerleave", onLeave);
    }

    const RADIUS = 190;

    const draw = (time: number) => {
      ctx.clearRect(0, 0, w, h);

      const t = time * 0.00035;
      const p = pointer.current;

      for (let cy = 0; cy < rows; cy++) {
        const y = cy * CELL;
        for (let cx = 0; cx < cols; cx++) {
          const x = cx * CELL;

          // Ambient diagonal wave: a slow band of brightness crossing the field.
          const wave = Math.sin((cx + cy) * 0.28 - t * 2.2);
          let alpha = BASE_ALPHA + Math.max(0, wave) * 0.055;

          // Fade the lattice out toward the bottom so text sits on clean ground.
          alpha *= 1 - Math.min(1, (y / h) * 0.75);

          let size = 2;
          let hue = 187; // wordmark cyan at rest

          if (p.active) {
            const dx = x - p.x;
            const dy = y - p.y;
            const d = Math.sqrt(dx * dx + dy * dy);
            if (d < RADIUS) {
              const lift = (1 - d / RADIUS) ** 2;
              alpha += lift * 0.72;
              size += lift * 3.4;
              hue = 187 - lift * 22; // shifts toward the signal green under the cursor
            }
          }

          if (alpha < 0.012) continue;

          ctx.fillStyle = `hsla(${hue}, 88%, 62%, ${Math.min(alpha, 0.9)})`;
          ctx.fillRect(x, y, size, size);
        }
      }

      if (running && !reduced) raf = requestAnimationFrame(draw);
    };

    if (reduced) {
      draw(0);
    } else {
      raf = requestAnimationFrame(draw);
    }

    const onVisibility = () => {
      if (document.hidden) {
        running = false;
        cancelAnimationFrame(raf);
      } else if (!reduced && !running) {
        running = true;
        raf = requestAnimationFrame(draw);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("pointermove", onPointer);
      window.removeEventListener("pointerleave", onLeave);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="absolute inset-0 h-full w-full pointer-events-none"
    />
  );
};

export default SignalField;
